// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { AddressFieldDisplayCollectionModel, CountryModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { placeHolders } from "@steiner/XDL/constants";
import { XAddressModel } from "@steiner/XDL/types";
import { FormikProps } from "formik";
import React, { forwardRef, Ref, useEffect, useState } from "react";
import { css } from "styled-components";

const stateIsRequired = translate("State is a Required Field");
const countryIsRequired = translate("Country is a Required Field");
interface OwnProps {
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    extendedStyles?: CustomCustomerAddressFormStyles;
    address?: XAddressModel;
    formId: string;
    title?: string;
    onUseBillingAddress?: (useAsBillingAddress: boolean) => void;
    useBillingAddress?: boolean;
    onAddressChange: (address: XAddressModel) => void;
    onCancel?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onSubmit?: (event: React.FormEvent<HTMLButtonElement>) => void;
}

type Props = OwnProps;

export interface CustomCustomerAddressFormStyles {
    container?: GridContainerProps;
    firstNameGridItem?: GridItemProps;
    firstNameText?: TextFieldPresentationProps;
    lastNameGridItem?: GridItemProps;
    lastNameText?: TextFieldPresentationProps;
    companyNameGridItem?: GridItemProps;
    companyNameText?: TextFieldPresentationProps;
    attentionGridItem?: GridItemProps;
    attentionText?: TextFieldPresentationProps;
    address1GridItem?: GridItemProps;
    address1Text?: TextFieldPresentationProps;
    address2GridItem?: GridItemProps;
    address2Text?: TextFieldPresentationProps;
    address3GridItem?: GridItemProps;
    address3Text?: TextFieldPresentationProps;
    address4GridItem?: GridItemProps;
    address4Text?: TextFieldPresentationProps;
    countryGridItem?: GridItemProps;
    countrySelect?: SelectPresentationProps;
    cityGridItem?: GridItemProps;
    cityText?: TextFieldPresentationProps;
    stateGridItem?: GridItemProps;
    stateSelect?: SelectPresentationProps;
    postalCodeGridItem?: GridItemProps;
    postalCodeText?: TextFieldPresentationProps;
    phoneGridItem?: GridItemProps;
    phoneText?: TextFieldPresentationProps;
    faxGridItem?: GridItemProps;
    faxText?: TextFieldPresentationProps;
    emailGridItem?: GridItemProps;
    emailText?: TextFieldPresentationProps;
    formButtonsWrapper?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const customCustomerAddressFormStyles: CustomCustomerAddressFormStyles = {};

const CustomCustomerAddressForm = (
    {
        title,
        onUseBillingAddress,
        useBillingAddress,
        formId,
        address,
        onAddressChange,
        countries,
        fieldDisplay,
        extendedStyles,
        onSubmit,
        onCancel,
    }: Props,
    ref: Ref<FormikProps<XAddressModel>>,
) => {
    const validation = address?.validation;
    const [styles] = React.useState(() => mergeToNew(customCustomerAddressFormStyles, extendedStyles));
    const [countryError, setCountryError] = useState("");
    const [stateError, setStateError] = useState("");

    // XNG-Changes
    useEffect(() => {
        if (countries.length === 1) {
            const selectedCountry = countries[0];
            changeHandler("country", selectedCountry);
        }
    }, [countries]);

    if (!address || !countries || !validation) {
        return null;
    }

    const submitHandler = (e: any) => {
        if (!address.state?.id) {
            setStateError(stateIsRequired);
            return;
        }
        onSubmit && onSubmit(e);
    };

    const changeHandler = (field: string, value: any) => {
        if (field === "postalCode") {
            value = value
                .replace(/[^\d]/g, "")
                .replace(/^(\d{5})(\d{1,4})?.*$/, "$1-$2")
                .replace(/-$/, "");
        }
        const newAddress = {
            ...(address || ({} as any)),
            [field]: value,
        };
        onAddressChange(newAddress);
    };
    return (
        <>
            <GridContainer {...styles.container} data-test-selector="customerAddressFormFields">
                {title && (
                    <GridItem width={12}>
                        <Typography variant="h3">{title}</Typography>
                    </GridItem>
                )}
                {onUseBillingAddress && (
                    <GridItem
                        width={12}
                        css={css`
                            height: 30px;
                        `}
                    >
                        <Checkbox checked={useBillingAddress} onChange={(_, value) => onUseBillingAddress(value)}>
                            {translate("Use billing address")}
                        </Checkbox>
                    </GridItem>
                )}
                {fieldDisplay.firstName!.isVisible && (
                    <GridItem {...styles.firstNameGridItem}>
                        <TextField
                            {...styles.firstNameText}
                            onChange={e => {
                                changeHandler("firstName", e.target.value);
                            }}
                            value={address.firstName}
                            label={fieldDisplay.firstName!.displayName}
                            placeholder={fieldDisplay.firstName!.displayName}
                            disabled={validation.firstName!.isDisabled || useBillingAddress}
                            required={validation.firstName!.isRequired}
                            maxLength={validation.firstName!.maxLength || undefined}
                            data-test-selector="customerAddressForm_firstName"
                            type="firstName"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.lastName!.isVisible && (
                    <GridItem {...styles.lastNameGridItem}>
                        <TextField
                            {...styles.lastNameText}
                            onChange={e => {
                                changeHandler("lastName", e.target.value);
                            }}
                            value={address.lastName}
                            label={fieldDisplay.lastName!.displayName}
                            placeholder={fieldDisplay.lastName!.displayName}
                            disabled={validation.lastName!.isDisabled || useBillingAddress}
                            required={validation.lastName!.isRequired}
                            maxLength={validation.lastName!.maxLength || undefined}
                            data-test-selector="customerAddressForm_lastName"
                            type="lastName"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.companyName!.isVisible && (
                    <GridItem {...styles.companyNameGridItem}>
                        <TextField
                            {...styles.companyNameText}
                            onChange={e => {
                                changeHandler("companyName", e.target.value);
                            }}
                            value={address.companyName}
                            label={fieldDisplay.companyName!.displayName}
                            placeholder={fieldDisplay.companyName!.displayName}
                            disabled={validation.companyName!.isDisabled || useBillingAddress}
                            required={validation.companyName!.isRequired}
                            maxLength={validation.companyName!.maxLength || undefined}
                            data-test-selector="customerAddressForm_companyName"
                            type="companyName"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.attention!.isVisible && (
                    <GridItem {...styles.attentionGridItem}>
                        <TextField
                            {...styles.attentionText}
                            onChange={e => {
                                changeHandler("attention", e.target.value);
                            }}
                            value={address.attention}
                            label={fieldDisplay.attention!.displayName}
                            placeholder={fieldDisplay.attention!.displayName}
                            disabled={validation.attention!.isDisabled || useBillingAddress}
                            required={validation.attention!.isRequired}
                            maxLength={validation.attention!.maxLength || undefined}
                            data-test-selector="customerAddressForm_attention"
                            type="companyName"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.address1!.isVisible && (
                    <GridItem {...styles.address1GridItem}>
                        <TextField
                            {...styles.address1Text}
                            onChange={e => {
                                changeHandler("address1", e.target.value);
                            }}
                            value={address.address1}
                            label={fieldDisplay.address1!.displayName}
                            placeholder={fieldDisplay.address1!.displayName}
                            disabled={validation.address1!.isDisabled || useBillingAddress}
                            required={validation.address1!.isRequired}
                            maxLength={validation.address1!.maxLength || undefined}
                            data-test-selector="customerAddressForm_address1"
                            type="address"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.address2!.isVisible && (
                    <GridItem {...styles.address2GridItem}>
                        <TextField
                            {...styles.address2Text}
                            onChange={e => {
                                changeHandler("address2", e.target.value);
                            }}
                            value={address.address2}
                            label={fieldDisplay.address2!.displayName}
                            placeholder={fieldDisplay.address2!.displayName}
                            disabled={validation.address2!.isDisabled || useBillingAddress}
                            required={validation.address2!.isRequired}
                            maxLength={validation.address2!.maxLength || undefined}
                            data-test-selector="customerAddressForm_address2"
                            type="address"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.address3!.isVisible && (
                    <GridItem {...styles.address3GridItem}>
                        <TextField
                            {...styles.address3Text}
                            onChange={e => {
                                changeHandler("address3", e.target.value);
                            }}
                            value={address.address3}
                            label={fieldDisplay.address3!.displayName}
                            placeholder={fieldDisplay.address3!.displayName}
                            disabled={validation.address3!.isDisabled || useBillingAddress}
                            required={validation.address3!.isRequired}
                            maxLength={validation.address3!.maxLength || undefined}
                            data-test-selector="customerAddressForm_address3"
                            type="address"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.address4!.isVisible && (
                    <GridItem {...styles.address4GridItem}>
                        <TextField
                            {...styles.address4Text}
                            onChange={e => {
                                changeHandler("address4", e.target.value);
                            }}
                            value={address.address4}
                            label={fieldDisplay.address4!.displayName}
                            placeholder={fieldDisplay.address4!.displayName}
                            disabled={validation.address4!.isDisabled || useBillingAddress}
                            required={validation.address4!.isRequired}
                            maxLength={validation.address4!.maxLength || undefined}
                            data-test-selector="customerAddressForm_address4"
                            type="address"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.country!.isVisible && countries.length > 1 && (
                    <GridItem {...styles.countryGridItem}>
                        <Select
                            {...styles.countrySelect}
                            label={fieldDisplay.country!.displayName}
                            disabled={validation.country!.isDisabled || countries.length === 1 || useBillingAddress}
                            required={validation.country!.isRequired}
                            data-test-selector="customerAddressForm_country"
                            onChange={e => {
                                const selectedCountry = countries.find(o => o.id === e.target.value);
                                if (!selectedCountry && validation.country!.isRequired) {
                                    setCountryError(countryIsRequired);
                                }
                                if (selectedCountry) {
                                    setCountryError("");
                                }
                                changeHandler("country", selectedCountry);
                            }}
                            value={address?.country?.id}
                            error={countryError}
                        >
                            <option value="">{translate("Select Country")}</option>
                            {countries.map(co => (
                                <option key={co.id} value={co.id}>
                                    {co.name}
                                </option>
                            ))}
                        </Select>
                    </GridItem>
                )}
                {fieldDisplay.city!.isVisible && (
                    <GridItem {...styles.cityGridItem}>
                        <TextField
                            {...styles.cityText}
                            onChange={e => {
                                changeHandler("city", e.target.value);
                            }}
                            value={address.city}
                            label={fieldDisplay.city!.displayName}
                            placeholder={fieldDisplay.city!.displayName}
                            disabled={validation.city!.isDisabled || useBillingAddress}
                            required={validation.city!.isRequired}
                            maxLength={validation.city!.maxLength || undefined}
                            data-test-selector="customerAddressForm_city"
                            type="city"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {countries && address?.country?.id && (
                    <GridItem {...styles.stateGridItem}>
                        <Select
                            {...styles.stateSelect}
                            label={fieldDisplay.state!.displayName}
                            disabled={validation.state!.isDisabled || useBillingAddress}
                            required
                            data-test-selector="customerAddressForm_state"
                            onChange={e => {
                                const selectedState = countries
                                    .find(curr => curr.id === address!.country!.id)
                                    ?.states?.find(o => o.id === e.target.value && e.target.value !== "");
                                if (!selectedState) {
                                    setStateError(stateIsRequired);
                                }
                                if (selectedState) {
                                    setStateError("");
                                }
                                changeHandler("state", selectedState);
                            }}
                            value={address.state?.id}
                            error={stateError}
                        >
                            <option value="">{translate("Select State")}</option>
                            {countries
                                .find(curr => curr.id === address!.country!.id)
                                ?.states?.map((o: any) => (
                                    <option key={o.id} value={o.id}>
                                        {o.name}
                                    </option>
                                ))}
                        </Select>
                    </GridItem>
                )}
                {fieldDisplay.postalCode!.isVisible && (
                    <GridItem {...styles.postalCodeGridItem}>
                        <TextField
                            {...styles.postalCodeText}
                            onChange={e => {
                                changeHandler("postalCode", e.target.value);
                            }}
                            value={address.postalCode}
                            label={fieldDisplay.postalCode!.displayName}
                            placeholder={fieldDisplay.postalCode!.displayName}
                            disabled={validation.postalCode!.isDisabled || useBillingAddress}
                            required={validation.postalCode!.isRequired}
                            maxLength={validation.postalCode!.maxLength || undefined}
                            data-test-selector="customerAddressForm_postalCode"
                            type="postalCodeUs"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.phone!.isVisible && (
                    <GridItem {...styles.phoneGridItem}>
                        <TextField
                            {...styles.phoneText}
                            onChange={e => {
                                changeHandler("phone", e.target.value);
                            }}
                            value={address.phone}
                            label={fieldDisplay.phone!.displayName}
                            placeholder={placeHolders.phone}
                            disabled={validation.phone!.isDisabled || useBillingAddress}
                            required={validation.phone!.isRequired}
                            maxLength={validation.phone!.maxLength || undefined}
                            data-test-selector="customerAddressForm_phone"
                            type="phone"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.fax!.isVisible && (
                    <GridItem {...styles.faxGridItem}>
                        <TextField
                            {...styles.faxText}
                            onChange={e => {
                                changeHandler("fax", e.target.value);
                            }}
                            value={address.fax}
                            label={fieldDisplay.fax!.displayName}
                            placeholder={placeHolders.phone}
                            disabled={validation.fax!.isDisabled || useBillingAddress}
                            required={validation.fax!.isRequired}
                            maxLength={validation.fax!.maxLength || undefined}
                            data-test-selector="customerAddressForm_fax"
                            type="phone"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {fieldDisplay.email!.isVisible && (
                    <GridItem {...styles.emailGridItem}>
                        <TextField
                            {...styles.emailText}
                            onChange={e => {
                                changeHandler("email", e.target.value);
                            }}
                            value={address.email}
                            label={fieldDisplay.email!.displayName}
                            placeholder={placeHolders.email}
                            disabled={validation.email!.isDisabled || useBillingAddress}
                            required={validation.email!.isRequired}
                            maxLength={validation.email!.maxLength || undefined}
                            data-test-selector="customerAddressForm_email"
                            type="email"
                            {...{ formId }}
                        />
                    </GridItem>
                )}
                {onSubmit && onCancel && (
                    <GridItem {...styles.formButtonsWrapper} className="form-buttons">
                        <Button variant="tertiary" onClick={onCancel}>
                            {translate("Previous")}
                        </Button>
                        <Button onClick={submitHandler} className="stepperNextButton-3">{translate("Next")}</Button>
                    </GridItem>
                )}
            </GridContainer>
        </>
    );
};

export default forwardRef(CustomCustomerAddressForm);
